import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles, Users, Award, ShieldCheck } from 'lucide-react';

/** Homepage hero — H1 carries the primary search theme; brand line is decorative. */
const TRUST = [
  { Icon: Users, value: '5,000+', label: 'Athletes coached', testId: 'trust-athletes' },
  { Icon: Award, value: '11', label: 'Coaches', testId: 'trust-coaches' },
  { Icon: ShieldCheck, value: '2020', label: 'Trusted since', testId: 'trust-since' },
];

export const HeroSection = () => (
  <section className="relative overflow-hidden bg-[#F9FAFB]" data-testid="hero-section">
    <img
      src="https://images.unsplash.com/photo-1461896836934-ffe607ba8211?crop=entropy&cs=srgb&fm=webp&w=1600&q=75"
      srcSet="https://images.unsplash.com/photo-1461896836934-ffe607ba8211?crop=entropy&cs=srgb&fm=webp&w=800&q=75 800w, https://images.unsplash.com/photo-1461896836934-ffe607ba8211?crop=entropy&cs=srgb&fm=webp&w=1200&q=75 1200w, https://images.unsplash.com/photo-1461896836934-ffe607ba8211?crop=entropy&cs=srgb&fm=webp&w=1600&q=75 1600w"
      sizes="100vw"
      width="1600"
      height="900"
      alt="Runners training on an athletics track with LGRA coaches in Kuala Lumpur, Malaysia"
      fetchPriority="high"
      loading="eager"
      decoding="async"
      className="absolute inset-0 w-full h-full object-cover object-[right_center]"
      data-testid="hero-bg-image"
    />
    <div className="absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(to right, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.9) 45%, rgba(255,255,255,0.4) 75%, rgba(255,255,255,0) 100%)' }} aria-hidden />
    <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse at 0% 0%, rgba(168,85,247,0.16) 0%, rgba(168,85,247,0) 55%)' }} aria-hidden />

    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 sm:py-16 md:py-24 relative">
      <div className="max-w-2xl">
        <div className="flex items-center gap-3 sm:gap-4 mb-5 sm:mb-6">
          <img src="/lgra-logo.webp" alt="LGRA – Little Grappy Running Academy logo" width="96" height="96" fetchPriority="high" decoding="async" className="h-14 sm:h-16 w-auto bg-white p-1.5 rounded-md shadow-lg shrink-0" />
          <p className="leading-tight min-w-0" data-testid="hero-brand">
            <span className="block text-[10px] sm:text-xs uppercase tracking-widest text-slate-500 font-bold">Little Grappy Running Academy</span>
            <span className="block text-base sm:text-xl font-black tracking-tight text-[#111827]">LGRA Running Academy · Malaysia</span>
          </p>
        </div>

        <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#F3E8FF] border border-[#A855F7]/40 text-[#4C1D95] text-[10px] sm:text-xs font-black uppercase tracking-widest rounded mb-4 sm:mb-5" data-testid="hero-kicker">
          <Sparkles size={14} /> Coaching for every runner — kids to marathoners
        </div>

        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-[#111827] tracking-tight leading-[1.05] mb-4 sm:mb-5" data-testid="hero-title">
          Running Coaching &amp; <span className="text-[#7E22CE]">Training Plans</span> in Malaysia
        </h1>

        <p className="text-sm sm:text-base md:text-lg text-slate-700 max-w-xl mb-6 sm:mb-8 leading-relaxed" data-testid="hero-description">
          Personalised online training plans, coached running classes in Kuala Lumpur and Shah Alam, technique coaching and a free running performance assessment — guided by LGRA’s coaching team since 2020.
        </p>

        <div className="flex flex-col sm:flex-row gap-3 mb-6 sm:mb-8">
          <Link to="/online-training-plan" className="inline-flex items-center justify-center gap-2 h-11 sm:h-12 px-6 sm:px-8 rounded-md bg-[#A855F7] hover:bg-[#7E22CE] text-white text-sm sm:text-base font-black tracking-tight w-full sm:w-auto transition-colors" data-testid="hero-primary-cta">
            <Sparkles size={16} /> Find My Best Training Plan <ArrowRight size={18} />
          </Link>
          <Link to="/running-classes" className="inline-flex items-center justify-center gap-2 h-11 sm:h-12 px-6 sm:px-8 rounded-md border-2 border-[#A855F7] text-[#7E22CE] hover:bg-[#F3E8FF] text-sm sm:text-base font-black tracking-tight w-full sm:w-auto transition-colors" data-testid="hero-secondary-cta">
            Explore Running Classes
          </Link>
        </div>

        <div className="grid grid-cols-3 gap-3 sm:gap-6 max-w-2xl" data-testid="hero-trust-bar">
          {TRUST.map(({ Icon, value, label, testId }) => (
            <div key={testId} className="flex items-start gap-2 sm:gap-3 min-w-0" data-testid={testId}>
              <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-md bg-[#F3E8FF] flex items-center justify-center flex-shrink-0"><Icon size={18} className="text-[#7E22CE]" /></div>
              <div className="min-w-0">
                <div className="text-xl sm:text-2xl md:text-3xl font-black text-[#7E22CE] leading-none">{value}</div>
                <div className="text-[9px] sm:text-[11px] uppercase tracking-widest text-slate-600 mt-1 font-bold">{label}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

export default HeroSection;
